import React, { useState } from 'react';
import { FiZap, FiBarChart2, FiGlobe, FiCpu, FiCode, FiShield, FiTrendingUp, FiLayers, FiArrowRight, FiCheckCircle, FiBookOpen } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import s from './Landing.module.css';

const EXAMPLE_MATCHES = [
  { league: 'Premier League', home: 'Manchester United', away: 'Manchester City', time: '15:00' },
  { league: 'La Liga', home: 'Barcelona', away: 'Real Madrid', time: '21:00' },
];

const EXAMPLE_CLASICOS = [
  { home: 'Manchester United', away: 'Real Madrid', odds: { h: '2.45', d: '3.30', a: '2.85' } },
  { home: 'Manchester City', away: 'Barcelona', odds: { h: '2.10', d: '3.55', a: '3.20' } },
  { home: 'Manchester United', away: 'Barcelona', odds: { h: '2.70', d: '3.15', a: '2.60' } },
  { home: 'Manchester City', away: 'Real Madrid', odds: { h: '2.25', d: '3.40', a: '3.05' } },
];

export default function Landing() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(API_BASE_URL + '/v1/login/', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) throw new Error('Invalid credentials');
      const data = await res.json();
      login(data);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.landing}>
      {/* ── Top Bar ── */}
      <div className={s.topBar}>
        <div className={s.logo}>
          <img className={s.logoImg} src={process.env.PUBLIC_URL + '/clasico_analytics.jpeg'} alt="Logo" />
          <span className={s.logoText}>Clasico Analytics</span>
        </div>
        <form className={s.authPanel} onSubmit={handleLogin}>
          <input className={s.authInput} type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
          <input className={s.authInput} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className={s.authBtn} type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign In'}</button>
          {error && <span className={s.authError}>{error}</span>}
        </form>
      </div>

      {/* ── Hero ── */}
      <section className={s.hero}>
        <div className={s.heroBadge}>B2B Betting Infrastructure</div>
        <h1 className={s.heroTitle}>
          The engine behind <span className={s.heroAccent}>cross-match</span> betting markets
        </h1>
        <p className={s.heroSubtitle}>
          Clasico Analytics is a B2B odds-as-a-service platform that generates entirely new betting
          markets by combining real matches into fictive cross-league encounters — powered by
          our proprietary pricing engine and delivered through a seamless API.
        </p>
        <div className={s.heroCtas}>
          <a href="#how-it-works" className={s.ctaPrimary}><FiArrowRight /> See How It Works</a>
          <Link to="/documentation" className={s.ctaSecondary}><FiCode /> API Documentation</Link>
          <Link to="/integrations/markets-catalog" className={s.ctaSecondary}><FiBookOpen /> Markets Catalog</Link>
        </div>
      </section>

      {/* ── What is Clasico ── */}
      <section className={s.section} id="how-it-works">
        <div className={s.sectionInner}>
          <div className={s.sectionLabel}>The Concept</div>
          <h2 className={s.sectionTitle}>What is a <span className={s.heroAccent}>Clasico</span> match?</h2>
          <p className={s.sectionDesc}>
            A Clasico is a <strong>fictive betting match</strong> created by combining teams from two different
            real matches happening on the same day. Instead of betting on Manchester United vs Manchester City
            or Barcelona vs Real Madrid separately, punters can bet on <em>Manchester United vs Real Madrid</em> —
            a match that doesn't exist on any pitch, but whose odds are intelligently generated by our proprietary pricing engine.
          </p>

          {/* Visual example */}
          <div className={s.exampleContainer}>
            <div className={s.exampleCol}>
              <div className={s.exampleHeader}>
                <span className={s.exampleDot} style={{ background: '#6c8cbf' }} />
                Real Matches — Same Day
              </div>
              {EXAMPLE_MATCHES.map((m, i) => (
                <div key={i} className={s.exampleMatch}>
                  <span className={s.exampleLeague}>{m.league}</span>
                  <div className={s.exampleTeams}>
                    <span className={s.exampleTeamName}>{m.home}</span>
                    <span className={s.exampleVs}>vs</span>
                    <span className={s.exampleTeamName}>{m.away}</span>
                  </div>
                  <span className={s.exampleTime}>{m.time}</span>
                </div>
              ))}
            </div>

            <div className={s.exampleArrow}>
              <div className={s.arrowLine} />
              <div className={s.arrowLabel}>Pricing Engine</div>
              <div className={s.arrowIcon}><FiCpu /></div>
              <div className={s.arrowLine} />
            </div>

            <div className={s.exampleCol}>
              <div className={s.exampleHeader}>
                <span className={s.exampleDot} style={{ background: '#73c991' }} />
                Generated Clasico Markets
              </div>
              {EXAMPLE_CLASICOS.map((c, i) => (
                <div key={i} className={s.exampleClasico}>
                  <div className={s.exampleTeams}>
                    <span className={s.exampleTeamName}>{c.home}</span>
                    <span className={s.exampleVs}>vs</span>
                    <span className={s.exampleTeamName}>{c.away}</span>
                  </div>
                  <div className={s.exampleOdds}>
                    <span className={s.oddChip}>1: {c.odds.h}</span>
                    <span className={s.oddChip}>X: {c.odds.d}</span>
                    <span className={s.oddChip}>2: {c.odds.a}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className={s.sectionNote}>
            From just <strong>2 real matches</strong>, our engine generates up to <strong>4 unique Clasico markets</strong> — each
            with full 1X2, Over/Under, Double Chance, European Handicap, Half-Time, BTTS, and First Goal odds.
            Every permutation of home/away teams creates a brand-new betting opportunity.
          </p>
        </div>
      </section>

      {/* ── Pricing Engine ── */}
      <section className={`${s.section} ${s.sectionAlt}`}>
        <div className={s.sectionInner}>
          <div className={s.sectionLabel}>Our Technology</div>
          <h2 className={s.sectionTitle}>Intelligent odds, delivered automatically</h2>
          <p className={s.sectionDesc}>
            Our new automated odds optimization engine dynamically adjusts prices in real time based on our
            database, ensuring consistent performance and optimal pricing across all matches and markets.
          </p>
          <div className={s.processSteps}>
            <div className={s.processStep}>
              <div className={s.stepNumber}>01</div>
              <h3 className={s.stepTitle}>Real Match Input</h3>
              <p className={s.stepDesc}>Source odds are ingested from real matches, giving the engine accurate and up-to-date data to work with.</p>
            </div>
            <div className={s.processStep}>
              <div className={s.stepNumber}>02</div>
              <h3 className={s.stepTitle}>Automated Pricing</h3>
              <p className={s.stepDesc}>Our engine processes the data and generates competitive, balanced odds for every Clasico market automatically.</p>
            </div>
            <div className={s.processStep}>
              <div className={s.stepNumber}>03</div>
              <h3 className={s.stepTitle}>Market Generation</h3>
              <p className={s.stepDesc}>50+ odds per Clasico are produced across 1X2, DC, O/U 2.5–6.5, EH, HT, BTTS, and First Goal markets.</p>
            </div>
            <div className={s.processStep}>
              <div className={s.stepNumber}>04</div>
              <h3 className={s.stepTitle}>Margin Management</h3>
              <p className={s.stepDesc}>Apply per-market margin overrides with full admin control over global defaults and per-match configurations.</p>
            </div>
            <div className={s.processStep}>
              <div className={s.stepNumber}>05</div>
              <h3 className={s.stepTitle}>Real-Time Optimization</h3>
              <p className={s.stepDesc}>Prices are continuously monitored and optimized to ensure consistent performance and market competitiveness.</p>
            </div>
            <div className={s.processStep}>
              <div className={s.stepNumber}>06</div>
              <h3 className={s.stepTitle}>Deliver via API</h3>
              <p className={s.stepDesc}>All Clasico markets, results, and history are exposed through a documented RESTful API ready for integration.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features grid ── */}
      <section className={s.section}>
        <div className={s.sectionInner}>
          <div className={s.sectionLabel}>Platform</div>
          <h2 className={s.sectionTitle}>Everything betting operators need</h2>
          <div className={s.features}>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiZap /></div>
              <h3 className={s.featureTitle}>Clasico Engine</h3>
              <p className={s.featureDesc}>Automated fictive match creation with one-click team selection from real match pools. Supports all permutations.</p>
            </div>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiBarChart2 /></div>
              <h3 className={s.featureTitle}>50+ Markets per Match</h3>
              <p className={s.featureDesc}>Full-time & half-time 1X2, Double Chance, O/U 2.5–6.5, European Handicap, BTTS, First Goal — all auto-calculated.</p>
            </div>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiTrendingUp /></div>
              <h3 className={s.featureTitle}>Real-Time Settlement</h3>
              <p className={s.featureDesc}>Winning odds are automatically determined when results come in. Full audit trail with quota history tracking.</p>
            </div>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiLayers /></div>
              <h3 className={s.featureTitle}>Margin Management</h3>
              <p className={s.featureDesc}>Granular margin controls per market type. Set global defaults or override per Clasico for maximum flexibility.</p>
            </div>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiGlobe /></div>
              <h3 className={s.featureTitle}>Multi-Language</h3>
              <p className={s.featureDesc}>Full i18n support for club names and entities. Serve localized content to operators in any market.</p>
            </div>
            <div className={s.featureCard}>
              <div className={s.featureIcon}><FiShield /></div>
              <h3 className={s.featureTitle}>Role-Based Access</h3>
              <p className={s.featureDesc}>Admin, employee, customer, and support roles with granular permissions. Secure token-based authentication.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Integration / API ── */}
      <section className={`${s.section} ${s.sectionAlt}`} id="integration">
        <div className={s.sectionInner}>
          <div className={s.sectionLabel}>For Developers</div>
          <h2 className={s.sectionTitle}>Integrate in hours, not months</h2>
          <p className={s.sectionDesc}>
            Our RESTful API lets betting operators pull Clasico markets directly into their platforms.
            All endpoints are documented, versioned, and secured with token-based authentication.
          </p>

          <div className={s.apiShowcase}>
            <div className={s.apiEndpoints}>
              <h3 className={s.apiTitle}>Key Endpoints</h3>
              <div className={s.endpointList}>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/markets-catalog/</code>
                  <span className={s.endpointDesc}>
                    Public catalog of every market. <Link to="/integrations/markets-catalog" style={{ color: 'var(--accent)' }}>Browse it →</Link>
                  </span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/documentation/</code>
                  <span className={s.endpointDesc}>
                    Full endpoint reference. <Link to="/documentation" style={{ color: 'var(--accent)' }}>View docs →</Link>
                  </span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/clasico/</code>
                  <span className={s.endpointDesc}>List all Clasico matches with full odds</span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/clasico/{'{id}'}</code>
                  <span className={s.endpointDesc}>Single Clasico with all market data</span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/clasico/?status=pending</code>
                  <span className={s.endpointDesc}>Filter by status (draft, pending, started, finished)</span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/clasico/stats</code>
                  <span className={s.endpointDesc}>Dashboard stats (total, finished, pending)</span>
                </div>
                <div className={s.endpoint}>
                  <span className={s.methodBadge} data-method="get">GET</span>
                  <code>/api/v1/quota-history/fictive-match/{'{id}'}</code>
                  <span className={s.endpointDesc}>Full audit trail of odds changes</span>
                </div>
              </div>
            </div>

            <div className={s.apiSample}>
              <h3 className={s.apiTitle}>Sample Response</h3>
              <pre className={s.codeBlock}>
{`{
  "id": 42,
  "matchTime": "2026-03-15T21:00:00+01:00",
  "statusId": 1,
  "teams": [
    {
      "name": "Manchester United",
      "side": "Home",
      "ttft15o": 2.15,
      "ttft15u": 1.68
    },
    {
      "name": "Real Madrid",
      "side": "Away",
      "ttft15o": 1.95,
      "ttft15u": 1.82
    }
  ],
  "quota": {
    "rh": 2.45, "rd": 3.30, "ra": 2.85,
    "dc1x": 1.38, "dc12": 1.32, "dcx2": 1.52,
    "ou25o": 1.75, "ou25u": 2.05,
    "ou35o": 2.40, "ou35u": 1.55,
    "eh10r1": 1.85, "eh10rx": 3.60,
    "rhth": 2.80, "rhtd": 2.45,
    "htBttsYes": 3.10, "htBttsNo": 1.32
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why operators choose us ── */}
      <section className={s.section}>
        <div className={s.sectionInner}>
          <div className={s.sectionLabel}>Why Us</div>
          <h2 className={s.sectionTitle}>A new revenue stream for betting operators</h2>
          <div className={s.benefitGrid}>
            {[
              { icon: <FiTrendingUp />, title: 'Incremental Revenue', desc: 'Offer markets that no competitor has. Clasico matches are exclusive — they don\'t exist on any real pitch.' },
              { icon: <FiZap />, title: 'Zero Fixture Dependency', desc: 'Generate new betting events on any match day. No waiting for fixtures — combine any two matches instantly.' },
              { icon: <FiLayers />, title: '50+ Markets Per Event', desc: 'Each Clasico ships with full 1X2, DC, O/U, EH, HT, BTTS, and First Goal markets — ready to offer.' },
              { icon: <FiCpu />, title: 'Intelligent Pricing', desc: 'Our automated engine dynamically adjusts odds in real time, ensuring optimal and consistent pricing across all markets.' },
              { icon: <FiCode />, title: 'Simple Integration', desc: 'RESTful API with JSON responses. Pull odds into your platform with standard HTTP calls. No SDK required.' },
              { icon: <FiShield />, title: 'Full Audit Trail', desc: 'Every odds change is logged with timestamps and user attribution. Full transparency for compliance.' },
            ].map((b, i) => (
              <div key={i} className={s.benefitItem}>
                <div className={s.benefitIcon}>{b.icon}</div>
                <div>
                  <h3 className={s.benefitTitle}>{b.title}</h3>
                  <p className={s.benefitDesc}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={`${s.section} ${s.sectionCta}`}>
        <div className={s.sectionInner} style={{ textAlign: 'center' }}>
          <h2 className={s.ctaTitle}>Ready to add Clasico markets to your platform?</h2>
          <p className={s.ctaDesc}>
            Get in touch with our team to discuss integration, pricing, and custom market configurations.
          </p>
          <div className={s.ctaChecks}>
            {['No upfront fees', 'Sandbox environment', 'Dedicated support', 'Custom margins'].map((t, i) => (
              <span key={i} className={s.ctaCheck}><FiCheckCircle /> {t}</span>
            ))}
          </div>
          <a href="mailto:info@clasico-analytics.com" className={s.ctaPrimary} style={{ marginTop: 24, display: 'inline-flex' }}>
            Contact Sales <FiArrowRight />
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <div className={s.footer}>
        <div className={s.footerInner}>
          <div className={s.footerBrand}>
            <img className={s.logoImg} src={process.env.PUBLIC_URL + '/clasico_analytics.jpeg'} alt="Logo" />
            <span className={s.logoText}>Clasico Analytics</span>
          </div>
          <div className={s.footerLinks}>
            <a href="#how-it-works">How It Works</a>
            <a href="#integration">API</a>
            <Link to="/documentation">API Docs</Link>
            <a href="mailto:info@clasico-analytics.com">Contact</a>
          </div>
          <div className={s.footerCopy}>
            &copy; {new Date().getFullYear()} Clasico Analytics. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}
